import React, { FC } from "react";
import { Box, FormControl, Label, Radio, RadioGroup, Text } from "@primer/react";
import { AlertIcon } from "@primer/octicons-react";

interface StatusContentProps {
  selectionCount: number;
  preflightCounts?: Record<string, number>;
  emailOption: string;
  onEmailOptionChange: (value: string) => void;
}

const STATUS_LABELS: Record<string, { label: string; variant: "success" | "attention" | "danger" | "default" }> = {
  confirmed: { label: "Confirmed", variant: "success" },
  pending_confirmation: { label: "Pending", variant: "attention" },
  cancelled: { label: "Cancelled", variant: "danger" },
  started: { label: "Started", variant: "default" },
  additional_payment_required: { label: "Additional Payment Required", variant: "attention" },
  deleting: { label: "Deleting", variant: "danger" },
};

interface StatusBreakdownProps {
  counts: Record<string, number>;
  targetStatus: string;
  describeRow?: (status: string, isAlreadyTarget: boolean) => string;
  // Statuses the action will skip. Their presence forces the breakdown to
  // render even for a uniform selection — otherwise a selection made up
  // entirely of skipped rows would show no warning at all (GR-2164).
  skippedStatuses?: string[];
}

const defaultDescribeRow = (targetLabel: string) =>
  (_status: string, isAlreadyTarget: boolean) =>
    isAlreadyTarget
      ? "No change will be made to these rows"
      : `These rows will be marked as ${targetLabel}`;

export const StatusBreakdown: FC<StatusBreakdownProps> = ({ counts, targetStatus, describeRow, skippedStatuses }) => {
  const entries = Object.entries(counts).filter(([, count]) => count > 0);
  const hasSkippedRows = entries.some(([status]) => skippedStatuses?.includes(status));
  if (entries.length <= 1 && !hasSkippedRows) return null;

  const headline = entries.length > 1
    ? "This selection contains rows with varying statuses"
    : "This action doesn't apply to the selected rows";
  const targetLabel = STATUS_LABELS[targetStatus]?.label || targetStatus;
  const description = describeRow || defaultDescribeRow(targetLabel);

  return (
    <Box
      sx={{
        borderRadius: "8px",
        border: "1px solid var(--borderColor-muted, #d1d9e0b3)",
        backgroundColor: "var(--bgColor-muted, #f6f8fa)",
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 2,
          mx: 3,
          mt: 3,
          mb: 2,
          px: "12px",
          py: "10px",
          borderRadius: "6px",
          backgroundColor: "var(--bgColor-attention-muted, #fff8c5)",
          border: "1px solid var(--borderColor-attention-muted, #d4a72c66)",
        }}
      >
        <Box sx={{ color: "var(--fgColor-attention, #9a6700)", flexShrink: 0, display: "flex" }}>
          <AlertIcon size={16} />
        </Box>
        <Text fontSize={1} fontWeight="semibold">
          {headline}
        </Text>
      </Box>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2, px: 3, pb: 3 }}>
        {entries.map(([status, count]) => {
          const config = STATUS_LABELS[status] || { label: status, variant: "default" as const };
          const isAlreadyTarget = status === targetStatus;
          const rowDescription = description(status, isAlreadyTarget);

          return (
            <Box
              key={status}
              sx={{
                px: 3,
                py: 2,
                borderRadius: "6px",
                backgroundColor: "var(--bgColor-default, #ffffff)",
                border: "1px solid var(--borderColor-muted, #d1d9e0b3)",
              }}
            >
              <Text as="div" fontSize={1} sx={{ mb: 1 }}>
                <Text as="span" fontWeight="bold">{count} {count === 1 ? "row" : "rows"}</Text>
                {" "}are currently marked as{" "}
                <Label variant={config.variant}>{config.label}</Label>
              </Text>
              <Text as="div" fontSize={1} sx={{ color: "var(--fgColor-muted, #656d76)" }}>
                {rowDescription}
              </Text>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

// Statuses RegistrationConfirmExecutor refuses to confirm — keep in sync with
// its actionable? check.
const CONFIRM_SKIPPED_STATUSES = ["cancelled", "deleting"];

const describeConfirmRow = (status: string, isAlreadyTarget: boolean) => {
  if (isAlreadyTarget) return "No change will be made to these rows";
  if (status === "cancelled") return "Cancelled registrations can't be confirmed — these rows will be skipped";
  if (status === "deleting") return "These registrations are being deleted and will be skipped";
  return "These rows will be marked as Confirmed";
};

const ConfirmedStatusContent: FC<StatusContentProps> = ({ selectionCount, preflightCounts, emailOption, onEmailOptionChange }) => {
  const hasMixedStatuses = preflightCounts && Object.values(preflightCounts).filter((c) => c > 0).length > 1;
  const cancelledCount = preflightCounts?.cancelled ?? 0;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      {preflightCounts && (
        <StatusBreakdown
          counts={preflightCounts}
          targetStatus="confirmed"
          describeRow={describeConfirmRow}
          skippedStatuses={CONFIRM_SKIPPED_STATUSES}
        />
      )}
      {cancelledCount > 0 && (
        <Text as="div" fontSize={1}>
          Cancelled registrations can&apos;t be confirmed here. To confirm a cancelled
          attendee, restore their registration from their attendee page first.
        </Text>
      )}
      <Box>
        <RadioGroup name="confirmation-email">
          <RadioGroup.Label>Confirmation email</RadioGroup.Label>
          {!hasMixedStatuses ? (
            <>
              <FormControl>
                <Radio
                  value="send"
                  checked={emailOption !== "none"}
                  onChange={() => onEmailOptionChange("unconfirmed_only")}
                />
                <FormControl.Label>Send email</FormControl.Label>
              </FormControl>
              <FormControl>
                <Radio
                  value="none"
                  checked={emailOption === "none"}
                  onChange={() => onEmailOptionChange("none")}
                />
                <FormControl.Label>Don&apos;t send email</FormControl.Label>
              </FormControl>
            </>
          ) : (
            <>
              <FormControl>
                <Radio
                  value="unconfirmed_only"
                  checked={emailOption === "unconfirmed_only"}
                  onChange={() => onEmailOptionChange("unconfirmed_only")}
                />
                <FormControl.Label>Send for unconfirmed rows only</FormControl.Label>
              </FormControl>
              <FormControl>
                <Radio
                  value="all"
                  checked={emailOption === "all"}
                  onChange={() => onEmailOptionChange("all")}
                />
                <FormControl.Label>Send for all</FormControl.Label>
              </FormControl>
              <FormControl>
                <Radio
                  value="none"
                  checked={emailOption === "none"}
                  onChange={() => onEmailOptionChange("none")}
                />
                <FormControl.Label>Don&apos;t send</FormControl.Label>
              </FormControl>
            </>
          )}
        </RadioGroup>
      </Box>
      <Text as="div" fontSize={1}>
        Are you sure you want to set{" "}
        <Text as="span" fontWeight="bold">{selectionCount} {selectionCount === 1 ? "attendee" : "attendees"}</Text>
        {" "}as confirmed?
      </Text>
    </Box>
  );
};

const CancelledStatusContent: FC<StatusContentProps> = ({ selectionCount, preflightCounts, emailOption, onEmailOptionChange }) => {
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      {preflightCounts && (
        <StatusBreakdown
          counts={preflightCounts}
          targetStatus="cancelled"
          skippedStatuses={["cancelled", "deleting"]}
        />
      )}
      <Box>
        <Text as="div" fontWeight="bold" fontSize={1} sx={{ mb: 1 }}>
          What happens when you cancel these registrations
        </Text>
        <Box as="ul" sx={{ m: 0, pl: 3 }}>
          <Box as="li"><Text as="div" fontSize={1}>The registration will be marked as cancelled</Text></Box>
          <Box as="li"><Text as="div" fontSize={1}>The attendee will receive a cancellation confirmation email if you select &ldquo;Send Cancellation Email&rdquo;</Text></Box>
          <Box as="li"><Text as="div" fontSize={1}>Any refunds must be handled manually</Text></Box>
        </Box>
      </Box>
      <Box>
        <RadioGroup name="cancellation-email">
          <RadioGroup.Label>Cancellation email</RadioGroup.Label>
          <FormControl>
            <Radio
              value="send"
              checked={emailOption !== "none"}
              onChange={() => onEmailOptionChange("all")}
            />
            <FormControl.Label>Send cancellation email</FormControl.Label>
          </FormControl>
          <FormControl>
            <Radio
              value="none"
              checked={emailOption === "none"}
              onChange={() => onEmailOptionChange("none")}
            />
            <FormControl.Label>Don&apos;t send email</FormControl.Label>
          </FormControl>
        </RadioGroup>
      </Box>
      <Text as="div" fontSize={1}>
        Are you sure you want to set{" "}
        <Text as="span" fontWeight="bold">{selectionCount} {selectionCount === 1 ? "attendee" : "attendees"}</Text>
        {" "}as cancelled?
      </Text>
    </Box>
  );
};

export const buildStatusContentMap = (
  selectionCount: number,
  preflightCounts: Record<string, number> | undefined,
  emailOption: string,
  onEmailOptionChange: (value: string) => void,
) => ({
  confirmed: {
    content: <ConfirmedStatusContent selectionCount={selectionCount} preflightCounts={preflightCounts} emailOption={emailOption} onEmailOptionChange={onEmailOptionChange} />,
    confirmButtonText: `Confirm ${selectionCount} ${selectionCount === 1 ? "attendee" : "attendees"}`,
  },
  cancelled: {
    content: <CancelledStatusContent selectionCount={selectionCount} preflightCounts={preflightCounts} emailOption={emailOption} onEmailOptionChange={onEmailOptionChange} />,
    confirmButtonText: `Cancel ${selectionCount} ${selectionCount === 1 ? "registration" : "registrations"}`,
    confirmButtonVariant: "danger" as const,
  },
});

export const STATUS_OPTIONS = [
  { label: "Confirmed", value: "confirmed" },
  { label: "Cancelled", value: "cancelled" },
];
